import { Add, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { FC, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Configuration,
  ExtendedFieldError,
  FormBlock,
  FormBlueprint,
} from "vmd-types";

import { importQuestionnaireFormBlocks } from "../../../helpers/excel-importer";
import { FormBlockEditor } from "./form-block-editor";
import { t } from "./labels";
import {
  buildConditionCandidates,
  conditionCandidateKey,
  ensureUniqueCodes,
  reuseExistingIds,
  withOrder,
} from "./utils";

// Drop any conditionalOn pointing at a question that no longer exists (deleted
// question or block) — a block's own conditionalOn included. Runs on every
// commit so a dangling reference can't persist.
const pruneDanglingConditions = (blocks: FormBlock[]): FormBlock[] => {
  const questionIds = new Set(
    blocks.flatMap((block) => (block.questions ?? []).map((q) => q.id))
  );
  return blocks.map((block) => ({
    ...block,
    conditionalOn:
      block.conditionalOn && !questionIds.has(block.conditionalOn.questionId)
        ? undefined
        : block.conditionalOn,
    questions: (block.questions ?? []).map((q) =>
      q.conditionalOn && !questionIds.has(q.conditionalOn.questionId)
        ? { ...q, conditionalOn: undefined }
        : q
    ),
  }));
};

// Older stored blueprints and some imports may miss question.fieldName; keep it
// aligned with the visible label so "Field name" is never empty after import.
const ensureQuestionFieldNames = (blocks: FormBlock[]): FormBlock[] =>
  blocks.map((block) => ({
    ...block,
    questions: (block.questions ?? []).map((question) => ({
      ...question,
      fieldName: question.fieldName?.trim()
        ? question.fieldName
        : (question.label ?? ""),
    })),
  }));

// Stable identity so a blueprint with no errors doesn't hand every question
// editor a fresh object and defeat their memoization.
const NO_ERRORS: Record<string, ExtendedFieldError> = {};

export interface FormBlocksSectionProps {
  language?: string;
}

export const FormBlocksSection: FC<FormBlocksSectionProps> = ({
  language = "en",
}) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton?: boolean;
  }>({ appStateSlice: "editSettingsForm" });

  const [expandedBlockId, setExpandedBlockId] = useState<string | false>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);

  const forms = formState.configuration?.trackingItemForms ?? [];
  const blueprint: FormBlueprint | undefined = forms[0];
  const blocks = useMemo(() => blueprint?.blocks ?? [], [blueprint?.blocks]);

  // Every question editor takes this list as a prop and renders a select over it.
  // Keying the memo on the content rather than on `blocks` keeps one identity
  // across edits the dropdown does not show, so those editors stay memoized.
  const candidateKey = conditionCandidateKey(blocks);
  const allQuestions = useMemo(
    () => buildConditionCandidates(blocks),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidateKey]
  );

  // Persist a new blocks array into the single blueprint (creating it if absent).
  const commitBlocks = (nextBlocks: FormBlock[]) => {
    const ordered = withOrder(
      pruneDanglingConditions(ensureQuestionFieldNames(nextBlocks))
    );
    const nextBlueprint: FormBlueprint = blueprint
      ? { ...blueprint, blocks: ordered }
      : { id: uuidv4(), name: "Tracking Item Form", blocks: ordered };
    const nextForms = [...forms];
    nextForms[0] = nextBlueprint;
    setValue("configuration.trackingItemForms", nextForms);
    if (formState.enableSaveButton !== true) {
      setValue("enableSaveButton", true);
    }
  };

  const handleImport = async () => {
    if (!importFile || importing) return;
    setImporting(true);
    setImportError(null);
    setImportedCount(null);
    try {
      const imported = await importQuestionnaireFormBlocks(importFile);
      // Re-run uniqueness over the merged set; the backend only dedupes within a
      // single import, so merging onto existing blocks can reintroduce clashes.
      // Keeping recognised ids is what lets already-stored answers survive.
      const merged = replaceExisting
        ? reuseExistingIds(blocks, imported)
        : [...blocks, ...imported];
      commitBlocks(ensureUniqueCodes(merged));
      setImportedCount(imported.length);
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  // One block open at a time; the opened block scrolls itself into view once its
  // transition finishes (see FormBlockEditor's onEntered).
  const toggleBlock = (blockId: string, isExpanded: boolean) => {
    setExpandedBlockId(isExpanded ? blockId : false);
  };

  const addBlock = () => {
    const block: FormBlock = {
      id: uuidv4(),
      title: "",
      order: blocks.length,
      enabled: true,
      questions: [],
    };
    commitBlocks([...blocks, block]);
    setExpandedBlockId(block.id);
  };

  const updateBlock = (index: number, block: FormBlock) => {
    const next = [...blocks];
    next[index] = block;
    commitBlocks(next);
  };

  const deleteBlock = (index: number) => {
    // A block can only be removed once it is empty; questions must be deleted first.
    if ((blocks[index]?.questions?.length ?? 0) > 0) return;
    const next = [...blocks];
    next.splice(index, 1);
    commitBlocks(next);
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    commitBlocks(next);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 500, width: "100%" }}>
        {t(language, "formBlocks.sectionTitle", "Form Blocks")}
      </Typography>
      <Typography sx={{ fontSize: 14, color: grey[600] }}>
        {t(
          language,
          "formBlocks.sectionDescription",
          "Define additional question blocks that appear on the tracking item form, below the basic data. Each block is a section of questions answered per application."
        )}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
          {t(language, "formBlocks.import.title", "Import from Excel")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: grey[600] }}>
          {t(
            language,
            "formBlocks.import.description",
            'Upload the VMD data-model workbook to generate blocks from the "Questionnaire Fields" sheet. Each group becomes a block and its fields become questions; the first group (the basic item) is skipped. Review and Save to persist.'
          )}
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={(e) => {
            setImportFile(e.target.files?.[0] ?? null);
            setImportError(null);
            setImportedCount(null);
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            sx={{ textTransform: "none", borderRadius: "4px" }}
          >
            {t(language, "formBlocks.import.upload", "Upload Excel")}
          </Button>

          {importFile && (
            <>
              <Typography sx={{ fontSize: 14, color: grey[700] }}>
                {importFile.name}
              </Typography>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={importing}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  color: "#fff",
                }}
              >
                {importing
                  ? t(language, "formBlocks.import.importing", "Importing…")
                  : t(language, "formBlocks.import.import", "Import")}
              </Button>
            </>
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              disabled={importing}
            />
          }
          label={t(
            language,
            "formBlocks.import.replace",
            "Delete existing form blocks before import"
          )}
          sx={{
            m: 0,
            ".MuiFormControlLabel-label": { fontSize: 14, color: grey[700] },
          }}
        />

        {importError && (
          // pre-line keeps one entity_ref failure per line.
          <Typography
            sx={{ fontSize: 14, color: "error.main", whiteSpace: "pre-line" }}
          >
            {importError}
          </Typography>
        )}
        {importedCount !== null && (
          <Typography sx={{ fontSize: 14, color: "success.main" }}>
            {t(
              language,
              "formBlocks.import.success",
              "Imported {count} block(s). Review below and click Save to persist."
            ).replace("{count}", String(importedCount))}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {blocks.length === 0 && (
          <Typography sx={{ fontSize: 14, color: grey[600] }}>
            {t(
              language,
              "formBlocks.emptyState",
              "No blocks yet. Add a block to start building the questionnaire."
            )}
          </Typography>
        )}

        {blocks.map((block, index) => (
          <FormBlockEditor
            key={block.id}
            block={block}
            formIndex={0}
            blockIndex={index}
            isFirst={index === 0}
            isLast={index === blocks.length - 1}
            expanded={expandedBlockId === block.id}
            onToggleExpanded={(isExpanded) => toggleBlock(block.id, isExpanded)}
            allQuestions={allQuestions}
            validationErrors={formState.validationErrors ?? NO_ERRORS}
            language={language}
            onChange={(updated) => updateBlock(index, updated)}
            onDelete={() => deleteBlock(index)}
            onMoveUp={() => moveBlock(index, -1)}
            onMoveDown={() => moveBlock(index, 1)}
          />
        ))}

        <Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addBlock}
            sx={{ textTransform: "none", borderRadius: "4px" }}
          >
            {t(language, "formBlocks.addBlock", "Add Block")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
